import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { post, patch } from "@/lib/api-client";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import type { Category } from "@/types/catalog";

interface Form {
  name: string; description: string;
  display_order: number; status: "active" | "inactive";
}

export function CategoryFormModal({
  open, onClose, category, onSaved,
}: { open: boolean; onClose: () => void; category: Category | null; onSaved: () => void }) {
  const isEdit = !!category;
  const { register, handleSubmit, formState: { errors } } = useForm<Form>({
    defaultValues: {
      name: category?.name ?? "",
      description: category?.description ?? "",
      display_order: category?.display_order ?? 0,
      status: category?.status ?? "active",
    },
  });

  const save = useMutation({
    mutationFn: (d: Form) => isEdit
      ? patch<Category>(`/admin/categories/${category!.id}`, d)
      : post<Category>("/admin/categories", d),
    onSuccess: () => {
      toast.success(isEdit ? "Category updated" : "Category created");
      onSaved();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit Category" : "New Category"}>
      <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-4">
        <Input label="Name"
               error={errors.name?.message}
               {...register("name", { required: "Required" })} />
        <div>
          <label className="block text-sm font-medium text-surface-800 mb-1.5">Description</label>
          <textarea
            {...register("description")}
            className="w-full h-20 px-3.5 py-2.5 bg-white border border-surface-300 rounded-lg text-sm resize-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40 focus:outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Display order" type="number" {...register("display_order", { valueAsNumber: true })} />
          <Select label="Status" options={[
            { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" },
          ]} {...register("status")} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={save.isPending}>{isEdit ? "Update" : "Create"}</Button>
        </div>
      </form>
    </Modal>
  );
}
